import * as React from 'react'
import { animate,motion,useMotionValue,useTransform } from 'framer-motion'
import { createFileRoute,Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card,CardContent,CardDescription,CardHeader,CardTitle } from "@/components/ui/card"
import { Package,ShoppingCart,Users,Banknote } from "lucide-react"
import { Area,AreaChart,CartesianGrid,XAxis,YAxis } from "recharts"
import {
ChartConfig,
ChartContainer,
ChartTooltip,
ChartTooltipContent,
} from "@/components/ui/chart"
import { requireAuth } from '@/lib/auth'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { getProducts } from '@/features/catalog/api'
import { customersApi } from '@/features/customers/api'
import { posApi } from '@/features/pos/api'
import { useSelectedShopId } from '@/features/shared'
import { formatCurrency } from '@/lib/formatters'
import { formatInStoreTz,formatSmartDate,useStoreTimeZone } from '@/lib/time'
import { queryKeys } from '@/lib/query-keys'
import type { PosReportRecord,PosReportRow } from '@/types'

export const Route = createFileRoute('/_dashboard/dashboard')({
  beforeLoad: requireAuth(),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: DashboardPage,
})

const chartConfig = {
  revenue: {
    label: "Revenue",
    color: "hsl(160 84% 39%)",
  },
} satisfies ChartConfig

function num(record: PosReportRecord | undefined, key: string): number {
  const v = record?.[key]
  return typeof v === 'number' ? v : 0
}

/// Six calendar months ago, as an ISO instant (chart window start).
function sixMonthsAgoIso(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 5)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/// Today 00:00 in the viewer's clock — close enough for a dashboard headline;
/// the Reports page offers precise store-timezone windows.
function todayStartIso(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

function buildMonthlyRevenue(rows: PosReportRow[], timeZone: string): Array<{ month: string; revenue: number }> {
  const byMonth = new Map<string, number>()
  for (const row of rows) {
    const completedAt = row.completedAt
    if (completedAt == null || typeof completedAt === 'boolean') continue
    const date = new Date(completedAt)
    if (Number.isNaN(date.getTime())) continue
    const key = formatInStoreTz(date, 'MMM yyyy', timeZone)
    const total = typeof row.totalCents === 'number' ? row.totalCents : 0
    byMonth.set(key, (byMonth.get(key) ?? 0) + total)
  }
  return Array.from(byMonth.entries()).map(([month, cents]) => ({ month, revenue: cents / 100 }))
}

/** Default whole-number formatter (locale grouping). Module-scope so the
 *  CountUpNumber ref callback stays stable across renders. */
function formatCount(n: number): string {
  return n.toLocaleString()
}

/**
 * Slow, ease-out count-up powered by framer-motion — no `useEffect`. A
 * MotionValue is animated by `animate()`, kicked off from a callback ref so the
 * frame loop starts on mount and restarts whenever `value` changes; framer maps
 * it to formatted text and renders it straight into the node (no React
 * re-render per frame). The ref returning `null` on unmount stops the tween.
 * The wrapper adds a blur-and-rise reveal, and `delay` staggers the cards so
 * the row cascades left → right.
 */
function CountUpNumber({
  value,
  format,
  delay = 0,
}: {
  value: number
  format: (n: number) => string
  delay?: number
}) {
  const count = useMotionValue(0)
  const text = useTransform(count, (v) => format(Math.round(v)))
  const controlsRef = React.useRef<ReturnType<typeof animate> | null>(null)

  const setNode = React.useCallback(
    (node: HTMLSpanElement | null) => {
      controlsRef.current?.stop()
      if (!node) return
      count.set(0)
      controlsRef.current = animate(count, value, {
        duration: 2.8,
        delay,
        // easeOutExpo — races out of the gate, then eases into the final figure.
        ease: [0.16, 1, 0.3, 1],
      })
    },
    [value, count, delay],
  )

  return (
    <motion.span
      className="inline-block"
      initial={{ opacity: 0, y: 14, filter: 'blur(6px)', scale: 0.96 }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.span ref={setNode}>{text}</motion.span>
    </motion.span>
  )
}

function StatCard({
  title,
  value,
  format = formatCount,
  loading,
  hint,
  icon: Icon,
  delay = 0,
}: {
  title: string
  value: number
  format?: (n: number) => string
  loading: boolean
  hint: string
  icon: typeof Package
  delay?: number
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="w-5 h-5 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold tabular-nums">
          {loading ? (
            // Blurred ghost figure while loading — the real number then sharpens
            // into focus (CountUpNumber's reveal also starts from a blur).
            <span aria-hidden className="inline-block select-none blur-[7px] opacity-50 animate-pulse">
              {format(1234)}
            </span>
          ) : (
            <CountUpNumber value={value} format={format} delay={delay} />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  )
}

function DashboardPage() {
  const shopId = useSelectedShopId()
  const timeZone = useStoreTimeZone()

  const todayReport = useQuery({
    queryKey: queryKeys.posXReport({ shopId, startAt: todayStartIso().slice(0, 10) }),
    queryFn: () => posApi.reports.getX({ shopId, startAt: todayStartIso() }),
    enabled: !!shopId,
    staleTime: 60_000,
  })
  const salesWindow = useQuery({
    queryKey: queryKeys.posSalesReport({ shopId, startAt: sixMonthsAgoIso().slice(0, 10) }),
    queryFn: () => posApi.reports.getSales({ shopId, startAt: sixMonthsAgoIso() }),
    enabled: !!shopId,
    staleTime: 5 * 60_000,
  })
  const productsCount = useQuery({
    queryKey: queryKeys.products('dashboard-count', { limit: 1 }),
    queryFn: () => getProducts({ limit: 1 }),
    staleTime: 5 * 60_000,
  })
  const customersCount = useQuery({
    queryKey: queryKeys.customers({ limit: 1, purpose: 'dashboard-count' }),
    queryFn: () => customersApi.customers.list({ limit: 1 }),
    staleTime: 5 * 60_000,
  })

  const x = todayReport.data
  const salesRows = salesWindow.data ?? []
  const monthly = buildMonthlyRevenue(salesRows, timeZone)
  const recentOrders = salesRows.slice(0, 6)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground mt-1">Welcome back! Here's what's happening today.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Orders today"
          value={num(x, 'salesCount')}
          loading={todayReport.isLoading}
          hint="Completed sales since midnight"
          icon={ShoppingCart}
          delay={0}
        />
        <StatCard
          title="Net sales today"
          value={num(x, 'netSalesCents')}
          format={formatCurrency}
          loading={todayReport.isLoading}
          hint={`Gross ${formatCurrency(num(x, 'grossSalesCents'))} · tips ${formatCurrency(num(x, 'tipCents'))}`}
          icon={Banknote}
          delay={0.12}
        />
        <StatCard
          title="Products"
          value={productsCount.data?.meta?.total ?? 0}
          loading={productsCount.isLoading}
          hint="Active catalog entries"
          icon={Package}
          delay={0.24}
        />
        <StatCard
          title="Customers"
          value={customersCount.data?.meta?.total ?? 0}
          loading={customersCount.isLoading}
          hint="Registered profiles"
          icon={Users}
          delay={0.36}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue Overview</CardTitle>
          <CardDescription>Completed-order revenue for the last 6 months (store time).</CardDescription>
        </CardHeader>
        <CardContent>
          {salesWindow.isLoading ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading revenue…</p>
          ) : monthly.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No completed sales in the last 6 months yet.</p>
          ) : (
            <ChartContainer config={chartConfig} className="max-h-[300px] w-full">
              <AreaChart accessibilityLayer data={monthly} margin={{ left: 12, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} width={70} tickFormatter={(v: number) => formatCurrency(Math.round(v * 100))} />
                <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                <defs>
                  <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <Area dataKey="revenue" type="natural" fill="url(#fillRevenue)" fillOpacity={0.4} stroke="var(--color-revenue)" />
              </AreaChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <CardDescription>Latest completed sales. <Link to="/sales" className="underline underline-offset-2">View all</Link></CardDescription>
          </CardHeader>
          <CardContent>
            {salesWindow.isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : recentOrders.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No sales yet.</p>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((row) => (
                  <div key={String(row.id)} className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                    <div>
                      <p className="font-medium">{String(row.number ?? 'Order')}</p>
                      <p className="text-sm text-muted-foreground">
                        {typeof row.completedAt === 'string' || typeof row.completedAt === 'number'
                          ? formatSmartDate(row.completedAt)
                          : '—'}
                      </p>
                    </div>
                    <span className="font-semibold text-primary tabular-nums">
                      {formatCurrency(typeof row.totalCents === 'number' ? row.totalCents : 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tenders Today</CardTitle>
            <CardDescription>How customers paid since midnight.</CardDescription>
          </CardHeader>
          <CardContent>
            {todayReport.isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : (
              <TenderList record={x} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function TenderList({ record }: { record: PosReportRecord | undefined }) {
  const raw = record?.tenderBreakdown
  const rows = Array.isArray(raw)
    ? raw.map((r) => {
        const row = (r ?? {}) as Record<string, unknown>
        return {
          type: typeof row.type === 'string' ? row.type : '—',
          count: typeof row.count === 'number' ? row.count : 0,
          amountCents: typeof row.amountCents === 'number' ? row.amountCents : 0,
        }
      })
    : []
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No payments taken today yet.</p>
  }
  return (
    <div className="space-y-3">
      {rows.map((t) => (
        <div key={t.type} className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
          <div>
            <p className="font-medium">{t.type}</p>
            <p className="text-sm text-muted-foreground">{t.count} payment{t.count === 1 ? '' : 's'}</p>
          </div>
          <span className="font-semibold text-primary tabular-nums">{formatCurrency(t.amountCents)}</span>
        </div>
      ))}
    </div>
  )
}
